import React from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { MessageCircle, MessageSquare, Trash2, AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useWorkflowStore } from '../store';
import { NodeActionPopover } from './NodeActionPopover';
import { AgentSummary } from '../api';

/** A step that sends a WhatsApp or SMS message instead of placing a call.
 *
 *  One component serves both channels — the type (`whatsapp` / `sms`) is read
 *  off the React Flow node and decides the icon, label and which `data` block
 *  (`data.whatsapp` / `data.sms`) holds the message. It fires on the edge
 *  conditions leading into it, exactly like an email step.
 */
export function MessageNode({ id, type, data, selected, positionAbsoluteX, positionAbsoluteY }: NodeProps) {
  const setNodes = useWorkflowStore(state => state.setNodes);
  const setEdges = useWorkflowStore(state => state.setEdges);
  const nodes = useWorkflowStore(state => state.nodes);
  const edges = useWorkflowStore(state => state.edges);
  const validationErrors = useWorkflowStore(state => state.validationErrors);

  const channel = type === 'sms' ? 'sms' : 'whatsapp';
  const Icon = channel === 'sms' ? MessageSquare : MessageCircle;
  const heading = channel === 'sms' ? 'SMS' : 'WhatsApp';
  // Real channel colours for the type badge — WhatsApp green, SMS blue.
  const badgeClass = channel === 'sms'
    ? 'bg-[#2563EB]/10 text-[#1D4ED8] border-[#2563EB]/30'
    : 'bg-[#25D366]/15 text-[#128C7E] border-[#25D366]/40';

  const config = ((data as any)?.[channel] as any) || {};
  const messageKind = channel === "whatsapp" && config.message_kind === "template" ? "template" : "free_text";
  const body = String(config.message_body || "").trim();
  const source = String(config.recipient_source || "customer_phone");
  const recipientField = String(config.recipient_field || "").trim();
  const templateId = String(config.template_id || "").trim();
  const templateName = String(config.template_name || config.template_snapshot?.name || "").trim();
  const templateMapping = (config.template_mapping as Record<string, string>) || {};
  const templateVars: number[] = (() => {
    const snapVars = (config.template_snapshot as any)?.variables;
    if (Array.isArray(snapVars) && snapVars.length) return snapVars.map((v: any) => Number(v)).filter(Number.isFinite);
    const m = String((config.template_snapshot as any)?.body || "").match(/\{\{(\d+)\}\}/g) || [];
    return [...new Set(m.map(s => Number(s.replace(/\D/g, ""))))].sort((a, b) => a - b);
  })();
  const templateIncomplete =
    !templateId || templateVars.some(v => !String((templateMapping as any)[String(v)] || "").trim());
  const incomplete = messageKind === "template"
    ? (!templateId || source === "outfield" && !recipientField || templateIncomplete)
    : (!body || (source === "outfield" && !recipientField));
  const error = validationErrors?.[id];

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes(nodes.filter(n => n.id !== id));
    setEdges(edges.filter(e => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-xl border bg-elevated text-left shadow-sm hover:shadow-md transition-all duration-200",
        error ? "border-danger ring-2 ring-danger/20"
          : selected ? "border-brand ring-2 ring-brand/20"
          : "border-line",
      )}
      style={{ width: 280 }}
    >
      <Handle type="target" position={Position.Left} className="w-1 h-1 opacity-0" />

      <div className="flex items-center justify-between px-3.5 py-3">
        <span className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
          badgeClass,
        )}>
          <Icon className="h-3 w-3" />
          {heading}
        </span>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 text-ink-3 hover:text-danger transition-colors"
          title="Remove step"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex flex-col gap-1.5 px-3.5 pb-3">
        <div className="text-[13px] font-bold text-ink truncate">
          {String((data as any)?.nodeLabel || `Send ${heading}`)}
        </div>
        {incomplete ? (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            {messageKind === "template" ? "Template incomplete — cannot publish" : "Message incomplete — cannot publish"}
          </span>
        ) : messageKind === "template" ? (
          <>
            <span className="truncate text-[11px] text-ink-3">
              To {source === "outfield" ? `{{${recipientField}}}` : "the customer's number"}
            </span>
            <span className="truncate text-[11px] font-medium text-ink-2">Template · {templateName || templateId}</span>
          </>
        ) : (
          <>
            <span className="truncate text-[11px] text-ink-3">
              To {source === "outfield" ? `{{${recipientField}}}` : "the customer's number"}
            </span>
            <span className="truncate text-[11px] font-medium text-ink-2">{body}</span>
          </>
        )}
      </div>

      <NodeActionPopover
        nodeId={id}
        positionX={positionAbsoluteX || 0}
        positionY={positionAbsoluteY || 0}
        availableAgents={(data as any).availableAgents as AgentSummary[]}
      />
    </div>
  );
}
